import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { LEGAL_LINKS } from "../lib/legal";

export default function Login() {
    const { user, login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (user && user !== null && user !== false) {
            const to = location.state?.from || "/dashboard";
            navigate(to, { replace: true });
        }
    }, [user, navigate, location]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            await login(email, password);
        } catch (e2) {
            setErr(e2.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid md:grid-cols-[2fr_3fr] bg-white" data-testid="login-page">
            {/* ── Pannello form (40%) ─────────────────────────────── */}
            <div className="flex flex-col justify-between p-8 md:p-14 lg:p-16 min-h-screen">
                {/* testata */}
                <div className="flex items-baseline justify-between border-b border-white/10 pb-4">
                    <img src="/logo-full.png" alt="Asta Intelligence" className="h-24 w-auto" />
                    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono-tight">
                        Ver. 2.6 — Italia
                    </div>
                </div>

                {/* form */}
                <div className="w-full max-w-sm py-10 fade-up">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#22D3EE] mb-3">
                        Area operatori
                    </div>
                    <h1 className="font-display-black text-4xl sm:text-5xl uppercase leading-[0.92] mb-3">
                        Accesso
                        <br />
                        riservato
                    </h1>
                    <p className="text-sm text-slate-400 mb-10">
                        Terminale di analisi quantitativa delle aste giudiziarie.
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 block mb-2">
                                Email
                            </label>
                            <input
                                data-testid="login-email-input"
                                type="email"
                                required
                                autoComplete="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full h-12 px-3 rounded-none border border-white/15 bg-white text-sm focus:border-[#22D3EE] focus:ring-0 focus:outline-none transition-colors"
                                placeholder="nome@studio.it"
                            />
                        </div>
                        <div>
                            <div className="flex items-baseline justify-between mb-2">
                                <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                    Password
                                </label>
                                <Link
                                    data-testid="forgot-password-link"
                                    to="/forgot-password"
                                    className="text-[11px] text-slate-500 hover:text-black underline decoration-1 underline-offset-4"
                                >
                                    Recupera credenziali
                                </Link>
                            </div>
                            <input
                                data-testid="login-password-input"
                                type="password"
                                required
                                autoComplete="current-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full h-12 px-3 rounded-none border border-white/15 bg-white text-sm focus:border-[#22D3EE] focus:ring-0 focus:outline-none transition-colors"
                                placeholder="••••••••"
                            />
                        </div>

                        {err && (
                            <div
                                data-testid="login-error"
                                className="rounded-none border border-red-300 bg-red-50 text-red-700 text-xs px-3 py-2.5"
                            >
                                {err}
                            </div>
                        )}

                        <button
                            data-testid="login-submit-button"
                            type="submit"
                            disabled={loading}
                            className="w-full h-12 rounded-none bg-black text-white text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#7C3AED] disabled:opacity-40 transition-colors"
                        >
                            {loading ? "Verifica in corso…" : "Accedi al terminale"}
                        </button>
                    </form>

                    <div className="mt-10 border-t border-white/10 pt-5 text-xs text-slate-400">
                        Non hai un account?{" "}
                        <Link
                            data-testid="goto-register-link"
                            to="/register"
                            className="font-semibold text-black underline decoration-1 underline-offset-4 hover:text-[#22D3EE]"
                        >
                            Richiedi accesso
                        </Link>
                    </div>
                </div>

                {/* piede */}
                <nav className="flex flex-wrap gap-x-5 gap-y-1 text-[11px] border-t border-white/10 pt-4">
                    {LEGAL_LINKS.map((l) => (
                        <Link
                            key={l.to}
                            to={l.to}
                            className="text-slate-500 hover:text-black underline decoration-1 underline-offset-4"
                        >
                            {l.label}
                        </Link>
                    ))}
                </nav>
            </div>

            {/* ── Pannello immagine (60%) ─────────────────────────── */}
            <div className="fd hidden md:block relative bg-[#04060B] overflow-hidden">
                <img
                    src="/italia-notte.jpg"
                    alt="L'Italia fotografata di notte dalla Stazione Spaziale Internazionale"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/20" />

                <div className="absolute top-0 left-0 right-0 flex items-center justify-between p-8 text-white/60">
                    <span className="text-[10px] uppercase tracking-[0.2em]">Copertura nazionale</span>
                    <span className="text-[10px] uppercase tracking-[0.2em] font-mono-tight">20 regioni · 106 tribunali</span>
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-8 lg:p-12 text-white">
                    <div className="font-display-black uppercase text-3xl lg:text-[2.6rem] leading-[0.95]">
                        Copertura nazionale.
                        <br />
                        Dati reali.
                        <br />
                        <span className="text-[#22D3EE]">Analisi multi-agente.</span>
                    </div>
                    <div className="mt-6 flex items-end justify-between gap-4 border-t border-white/15 pt-4">
                        <p className="text-[11px] text-white/50 max-w-md leading-relaxed">
                            Documenti giudiziari, rischio legale, urbanistica, valutazione di
                            mercato e piano d&apos;azione — in un unico report.
                        </p>
                        <p className="text-[10px] text-white/30 font-mono-tight whitespace-nowrap shrink-0">
                            42°N 12°E · Foto: NASA / ISS
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
